import {
  LifeReadinessSubcategory,
  LifeReadinessQuestion,
  LifeReadinessQuestionUserLevel,
  LifeReadinessCategory,
} from '@backrs_ui/common-ui';

export const getQuestionCountsForSubcategory = (
  subcategory: LifeReadinessSubcategory,
  questions: LifeReadinessQuestion[],
  userLevels: LifeReadinessQuestionUserLevel[],
) => {
  return countForSubcategory(subcategory, questions, userLevels);
};

type LevelCounts = Readonly<{
  level1_count: number;
  level2_count: number;
  level3_count: number;
  level4_count: number;
  level5_count: number; // not actually used in the UI, but we do have a 5 level description so it needs to match otherwise TS complains
  total: number;
}>;

export type CategoryProgressBars = Record<string, Omit<LevelCounts, 'level5_count'>>;

export type CategoryProgress =
  | { kind: 'empty' }
  | { kind: 'partial' }
  | { kind: 'complete'; progressBars: CategoryProgressBars };

/**
 * Count how many times each level was selected
 * for questions in a specific subcategory.
 */
function countForSubcategory(
  subcategory: LifeReadinessSubcategory,
  questions: LifeReadinessQuestion[],
  userLevels: LifeReadinessQuestionUserLevel[],
): LevelCounts {
  const counts = {
    level1_count: 0,
    level2_count: 0,
    level3_count: 0,
    level4_count: 0,
    level5_count: 0,
    total: 0,
  };

  // Collect the set of question-IDs belonging to this subcategory
  const questionIdsInSub = new Set(
    questions.filter((q) => q.life_survey_subcategory_id === subcategory.id).map((q) => q.id),
  );

  // Tally up userLevels whose question is in that set
  for (const ul of userLevels) {
    if (!questionIdsInSub.has(ul.life_survey_question_id)) continue;

    const key = `level${ul.level}_count` as keyof LevelCounts;

    counts[key] += 1;
  }

  counts.total = questionIdsInSub.size;

  return counts;
}

/**
 * Compute a category's progress as a discriminated union: empty (no answers),
 * partial (some answers), or complete (every question answered, with bars).
 */
export function getCategoryProgress(
  category: LifeReadinessCategory,
  allSubcategories: LifeReadinessSubcategory[],
  allQuestions: LifeReadinessQuestion[],
  allUserLevels: LifeReadinessQuestionUserLevel[],
): CategoryProgress {
  const subcategoriesInCategory = allSubcategories.filter(
    (sc) => sc.life_survey_category_id === category.id,
  );

  const nameToCounts = subcategoriesInCategory.map((subcategory) => ({
    name: subcategory.name,
    counts: countForSubcategory(subcategory, allQuestions, allUserLevels),
  }));

  const { totalAnswered, totalQuestions } = nameToCounts.reduce(
    (acc, { counts }) => ({
      totalAnswered:
        acc.totalAnswered +
        counts.level1_count +
        counts.level2_count +
        counts.level3_count +
        counts.level4_count,
      totalQuestions: acc.totalQuestions + counts.total,
    }),
    { totalAnswered: 0, totalQuestions: 0 },
  );

  if (totalAnswered === 0) return { kind: 'empty' };
  if (totalQuestions === 0 || totalAnswered < totalQuestions) return { kind: 'partial' };

  const progressBars: CategoryProgressBars = {};

  for (const { name, counts } of nameToCounts) {
    progressBars[name] = {
      level1_count: counts.level1_count,
      level2_count: counts.level2_count,
      level3_count: counts.level3_count,
      level4_count: counts.level4_count,
      total: counts.total,
    };
  }

  return { kind: 'complete', progressBars };
}
